import { ICapitalGood } from '../capital-good/capital-good.model';
import { IGoodCategory, IPostGoodCategory } from '../good-category/good-category.model';
import httpClient from "../http/http-client";
import { IManufacturer, IPostManufacturer } from '../manufacturer/manufacturer.model';
import { IOrganisationDetails } from '../organisation/organisation.model';
import { IUserDetails } from '../user-details/user-details.model';
import { IMessageResponse, ISuperAdminOrganisation, ISupplierMatchOverride, IPostSupplierMatchOverride, ICompanyHouseSearchResult } from './super-admin.model';

export default class SuperAdminService {
  http;

  constructor() {
    this.http = httpClient
  }

  // ----- Supplier match overrides (curated alias -> Companies House company) -----

  async getSupplierMatchOverrides(): Promise<ISupplierMatchOverride[]> {
    return this.http.get('super-admin/supplier-match-overrides')
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createSupplierMatchOverride(override: IPostSupplierMatchOverride): Promise<ISupplierMatchOverride> {
    return this.http.post('super-admin/supplier-match-override', override)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateSupplierMatchOverride(id: string, override: Partial<IPostSupplierMatchOverride>): Promise<ISupplierMatchOverride> {
    return this.http.patch(`super-admin/supplier-match-override/${id}`, override)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteSupplierMatchOverride(id: string): Promise<IMessageResponse> {
    return this.http.delete(`super-admin/supplier-match-override/${id}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async searchCompanyHouse(term: string): Promise<ICompanyHouseSearchResult[]> {
    return this.http.get(`super-admin/company-house-search?term=${encodeURIComponent(term)}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async resetOrganisation(): Promise<IMessageResponse> {
    return this.http.post('super-admin/reset-organisation', {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async joinUserOrganisation(organisationId: string): Promise<IMessageResponse> {
    return this.http.post('super-admin/update-organisation', { organisationId: organisationId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getAllOrganisations(): Promise<ISuperAdminOrganisation[]> {
    return this.http.get('super-admin/organisation')
      .then(response => {
        return response.data.rows
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }


  async getAllOrganisationsWithPagination(subscriptionActive: boolean, searchQuery: string, page: number, perPage: number = 10): Promise<{ count: number, rows: ISuperAdminOrganisation[] }> {
    return this.http.get(`super-admin/organisation?active=${subscriptionActive}&nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async toggleSubscription(organisationId: string): Promise<IMessageResponse> {
    return this.http.post('super-admin/toggle-subscription', { organisationId: organisationId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async togglePartner(organisationId: string): Promise<IMessageResponse> {
    return this.http.post('super-admin/toggle-partner', { organisationId: organisationId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getAllUserDetailsWithPagination(organisationId: string, status: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', queryString?: string): Promise<{ count: number, rows: IUserDetails[] }> {
    return this.http.get(`super-admin/organisation/${organisationId}/users?status=${status}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async editOrganisation(organisationId: string, organisation: IOrganisationDetails): Promise<IOrganisationDetails> {
    return this.http.patch(`super-admin/organisation/${organisationId}`, organisation)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getPartnerClientsWithPagination(organisationId: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IOrganisationDetails[] }> {
    return this.http.get(`super-admin/partner-clients/${organisationId}?page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async checkClientStatus(organisationId: string): Promise<any> {
    return this.http.get(`super-admin/check-client-status/${organisationId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getAllGoodsWithPagination(searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: ICapitalGood[] }> {
    return this.http.get(`super-admin/capital-good?nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async createGood(formData: FormData): Promise<ICapitalGood> {
    return this.http.post(`super-admin/capital-good`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateGood(capitalGoodId: string, formData: FormData): Promise<ICapitalGood> {
    return this.http.patch(`super-admin/capital-good/${capitalGoodId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async activateGood(capitalGoodId: string): Promise<ICapitalGood> {
    return this.http.patch(`super-admin/capital-good/${capitalGoodId}/activate`, { capitalGoodId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAllGoodCategoriesWithPagination(searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IGoodCategory[] }> {
    return this.http.get(`super-admin/good-categories?nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async createGoodCategory(goodCategory: IPostGoodCategory): Promise<IGoodCategory> {
    return this.http.post(`super-admin/good-category`, goodCategory)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateGoodCategory(goodCategoryId: string, goodCategory: IPostGoodCategory): Promise<IGoodCategory> {
    return this.http.patch(`super-admin/good-category/${goodCategoryId}`, goodCategory)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async activateGoodCategory(goodCategoryId: string): Promise<IGoodCategory> {
    return this.http.patch(`super-admin/good-category/${goodCategoryId}/activate`, { goodCategoryId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getAllManufacturersWithPagination(searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IManufacturer[] }> {
    return this.http.get(`super-admin/manufacturer?nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createManufacturer(manufacturer: IPostManufacturer): Promise<IManufacturer> {
    return this.http.post(`super-admin/manufacturer`, manufacturer)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateManufacturer(manufacturerId: string, manufacturer: IPostManufacturer): Promise<IManufacturer> {
    return this.http.patch(`super-admin/manufacturer/${manufacturerId}`, manufacturer)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async activateManufacturer(manufacturerId: string): Promise<IManufacturer> {
    return this.http.patch(`super-admin/manufacturer/${manufacturerId}/activate`, { manufacturerId })
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async toggleAllowApiAccess(organisationId: string): Promise<IMessageResponse> {
    return this.http.post(`super-admin/organisation/${organisationId}/toggle-api-access`, {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async toggleAiCopilotEnabled(organisationId: string): Promise<{ aiCopilotEnabled: boolean }> {
    return this.http.post(`super-admin/organisation/${organisationId}/toggle-ai-copilot`, {})
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async regenerateTransactions(data: { organisationId?: string, calendarYear?: number, scopes?: string[], includeSpendBased?: boolean }): Promise<{ countRegenerated: number }> {
    return this.http.post('super-admin/transactions/regenerate', data)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getAllFriendlyCategoryGroupsWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IFriendlyCategoryGroup[] }> {
    return this.http.get(`super-admin/friendly-category-groups?page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createFriendlyCategoryGroup(data: IPostFriendlyCategoryGroup): Promise<IFriendlyCategoryGroup> {
    return this.http.post('super-admin/friendly-category-group', data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateFriendlyCategoryGroup(id: string, data: IPostFriendlyCategoryGroup): Promise<IFriendlyCategoryGroup> {
    return this.http.patch(`super-admin/friendly-category-group/${id}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getAllFriendlyCategoriesWithPagination(page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc', friendlyCategoryGroupId?: string): Promise<{ count: number, rows: IFriendlyCategory[] }> {
    const groupFilter = friendlyCategoryGroupId ? `&friendlyCategoryGroupId=${friendlyCategoryGroupId}` : '';
    return this.http.get(`super-admin/friendly-categories?page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}${groupFilter}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async createFriendlyCategory(data: IPostFriendlyCategory): Promise<IFriendlyCategory> {
    return this.http.post('super-admin/friendly-category', data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async updateFriendlyCategory(id: string, data: IPostFriendlyCategory): Promise<IFriendlyCategory> {
    return this.http.patch(`super-admin/friendly-category/${id}`, data)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async activateFriendlyCategory(id: string): Promise<IFriendlyCategory> {
    return this.http.patch(`super-admin/friendly-category/${id}/activate`, {})
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // ----- Product-telemetry usage views -----

  async getUsageSummary(from?: string, to?: string, quietDays: number = 30): Promise<ISuperAdminUsageSummary> {
    const qs = new URLSearchParams({ quietDays: String(quietDays) });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this.http.get(`super-admin/usage/summary?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // page is 0-based, matching the other super-admin tables.
  async getOrganisationUsage(searchQuery: string, page: number, perPage: number = 25, from?: string, to?: string, quietDays: number = 30, opts?: { orderBy?: string; order?: 'asc' | 'desc'; quietOnly?: boolean }): Promise<{ count: number, rows: ISuperAdminOrgUsage[] }> {
    const qs = new URLSearchParams({ search: searchQuery, page: String(page), perPage: String(perPage), quietDays: String(quietDays) });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (opts?.orderBy) qs.set('orderBy', opts.orderBy);
    if (opts?.order) qs.set('order', opts.order);
    if (opts?.quietOnly) qs.set('quietOnly', 'true');
    return this.http.get(`super-admin/usage/organisations?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  // All matching rows (no pagination), for CSV export.
  async exportOrganisationUsage(from?: string, to?: string, quietDays: number = 30, opts?: { search?: string; orderBy?: string; order?: 'asc' | 'desc'; quietOnly?: boolean }): Promise<{ rows: ISuperAdminOrgUsage[] }> {
    const qs = new URLSearchParams({ quietDays: String(quietDays) });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    if (opts?.search) qs.set('search', opts.search);
    if (opts?.orderBy) qs.set('orderBy', opts.orderBy);
    if (opts?.order) qs.set('order', opts.order);
    if (opts?.quietOnly) qs.set('quietOnly', 'true');
    return this.http.get(`super-admin/usage/organisations-export?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async exportErrors(from?: string, to?: string): Promise<{ rows: ISuperAdminErrorRow[] }> {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this.http.get(`super-admin/usage/errors-export?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getOrganisationUsageDetail(organisationId: string, from?: string, to?: string): Promise<ISuperAdminOrgUsageDetail> {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this.http.get(`super-admin/usage/organisations/${organisationId}?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getFeatureUsage(from?: string, to?: string): Promise<ISuperAdminFeatureUsage> {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this.http.get(`super-admin/usage/features?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async getErrorSummary(from?: string, to?: string, page: number = 0, perPage: number = 25): Promise<ISuperAdminErrorSummary> {
    const qs = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return this.http.get(`super-admin/usage/errors?${qs.toString()}`)
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }

  async deleteOldErrors(olderThanDays: number = 60): Promise<{ deleted: number }> {
    return this.http.post('super-admin/usage/errors/delete-old', { olderThanDays })
      .then(response => response.data)
      .catch(err => { console.log(err); throw err; });
  }
}

export interface IFriendlyCategoryGroup {
  id: string;
  name: string;
  displayOrder: number;
  icon?: string;
}

export interface IPostFriendlyCategoryGroup {
  name: string;
  displayOrder?: number;
  icon?: string;
}

export interface IFriendlyCategory {
  id: string;
  name: string;
  friendlyName?: string;
  friendlyCategoryGroupId: string;
  friendlyCategoryGroup?: IFriendlyCategoryGroup;
  icon?: string;
  description?: string;
  displayOrder?: number;
  visibleToUser: boolean;
}

export interface IPostFriendlyCategory {
  name: string;
  friendlyName?: string;
  friendlyCategoryGroupId: string;
  icon?: string;
  description?: string;
  displayOrder?: number;
}

export interface ISuperAdminUsageSummary {
  windowDays: number;
  quietDays: number;
  totals: {
    totalOrganisations: number;
    activeOrganisations: number;
    events: number;
    suggestionOutcomes: number;
    quietOrganisations: number;
    /** Distinct users with a live session right now (an instantaneous gauge, not windowed). */
    signedInNow: number;
  };
  previous: {
    totalOrganisations: number;
    activeOrganisations: number;
    events: number;
    suggestionOutcomes: number;
  };
  eventsByType: { eventType: string; count: number }[];
  suggestionAcceptance: { domain: string; total: number; accepted: number; acceptanceRate: number }[];
}

export type ISuperAdminPlanStatus = 'SUBSCRIBED' | 'TRIAL' | 'EXPIRED';

export interface ISuperAdminOrgUsage {
  organisationId: string;
  name: string;
  eventCount: number;
  uploads: number;
  imports: number;
  reports: number;
  surveys: number;
  logins: number;
  errors: number;
  lastActivityAt: string | null;
  lastLoginAt: string | null;
  isQuiet: boolean;
  plan: ISuperAdminPlanStatus;
  trialDaysRemaining: number;
  trialEndsAt: string | null;
}

export interface ISuperAdminUsageEvent {
  eventType: string;
  occurredAt: string;
  source: string | null;
  context: { [key: string]: any } | null;
}

export interface ISuperAdminOrgUsageDetail {
  organisationId: string;
  name: string;
  windowDays: number;
  lastActivityAt: string | null;
  lastLoginAt: string | null;
  totals: { events: number; suggestionOutcomes: number; errors: number; logins: number };
  totalsPrev: { events: number; suggestionOutcomes: number; logins: number };
  /** Distinct users of this organisation with a live session right now. */
  signedInNow: number;
  plan: { status: ISuperAdminPlanStatus; trialDaysRemaining: number; trialEndsAt: string | null };
  settings: ISuperAdminOrgSettings;
  eventsByType: { eventType: string; count: number; previous: number }[];
  reportsByType: ISuperAdminReportView[];
  analyticsDashboardByTab: { tab: string; count: number }[];
  ssoByProvider: ISuperAdminSsoProvider[];
  importsByPath: ISuperAdminImportPath[];
  entitiesAdded: ISuperAdminEntitiesAdded;
  entitiesAddedPrev: ISuperAdminEntitiesAdded;
  suggestionAcceptance: { domain: string; total: number; accepted: number; acceptanceRate: number }[];
  recentEvents: ISuperAdminUsageEvent[];
  errorsByStatus: { statusCode: number; count: number }[];
  recentErrors: ISuperAdminErrorRow[];
}

export interface ISuperAdminErrorRow {
  occurredAt: string;
  statusCode: number;
  method: string | null;
  path: string | null;
  routePattern?: string | null;
  message: string | null;
  organisationId?: string | null;
  organisationName?: string | null;
}

export interface ISuperAdminErrorSummary {
  windowDays: number;
  total: number;
  byStatus: { statusCode: number; count: number }[];
  byEndpoint: { routePattern: string; method: string | null; count: number }[];
  byMessage: { message: string; count: number }[];
  recentCount: number;
  recent: ISuperAdminErrorRow[];
  page: number;
  perPage: number;
}

// One row of the "which reports were viewed" breakdown. analyticReportId/name are set
// only for ANALYTICS views, so the frontend can show "Analytics - <name>" per report.
export interface ISuperAdminReportView {
  reportType: string;
  analyticReportId: string | null;
  name: string | null;
  count: number;
}

export interface ISuperAdminImportPath { importType: string; viaBill: boolean; billType: string | null; uploaded: number; committed: number; prevCommitted?: number; }
export interface ISuperAdminSsoProvider { eventType: string; provider: string; count: number; }
export interface ISuperAdminEntitiesAdded { suppliers: number; vehicles: number; projects: number; products: number; }
export interface ISuperAdminOrgSettings { subsidiaries: boolean; projects: boolean; recs: boolean; flag: boolean; pcf: boolean; tndLosses: boolean; inflation: boolean; }
export interface ISuperAdminSettingsAdoption { totalOrganisations: number; subsidiaries: number; projects: number; recs: number; flag: number; pcf: number; tndLosses: number; inflation: number; }
export interface ISuperAdminFeatureCounts {
  dashboardsCreated: number;
  dashboardsViewed: number;
  hotspotViewed: number;
  reclassifications: number;
  ssoUsed: number;
  ssoEnabled: number;
  mfaEnabled: number;
}

export interface ISuperAdminFeatureUsage {
  windowDays: number;
  eventsByType: { eventType: string; count: number; previous: number }[];
  reportsByType: ISuperAdminReportView[];
  analyticsDashboardByTab: { tab: string; count: number }[];
  ssoByProvider: ISuperAdminSsoProvider[];
  topReports: { analyticReportId: string; name: string | null; count: number }[];
  importsByPath: ISuperAdminImportPath[];
  featureCounts: ISuperAdminFeatureCounts;
  featureCountsPrev: ISuperAdminFeatureCounts;
  settingsAdoption: ISuperAdminSettingsAdoption;
  entitiesAdded: ISuperAdminEntitiesAdded;
  entitiesAddedPrev: ISuperAdminEntitiesAdded;
}